import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface AuthPageProps {
  onSignIn: (email: string, password: string) => Promise<void>;
  onSignUp: (email: string, password: string) => Promise<void>;
}

export function AuthPage({ onSignIn, onSignUp }: AuthPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      await onSignIn(email, password);
      toast.success('Login realizado com sucesso!');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await onSignUp(email, password);
      toast.success('Conta criada com sucesso! Verifique seu email.');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-background to-success-50 dark:from-neutral-950 dark:via-background dark:to-neutral-900 p-4"
      data-testid="auth-page"
    >
      <Card className="w-full max-w-md" data-testid="auth-card">
        <CardHeader className="space-y-4 text-center">
          <div 
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground"
            aria-label="Logo Cash Flow Manager"
          >
            <Building2 className="h-8 w-8" aria-hidden="true" />
          </div>
          <div>
            <CardTitle className="text-2xl">Cash Flow Manager</CardTitle>
            <CardDescription>
              Sistema completo de gestão financeira empresarial
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full" data-testid="auth-tabs">
            <TabsList className="grid w-full grid-cols-2" role="tablist" aria-label="Opções de autenticação">
              <TabsTrigger value="signin" data-testid="auth-tab-signin">Entrar</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup">Criar Conta</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" data-testid="auth-signin-form">
              <form onSubmit={handleSignIn} className="space-y-4" aria-label="Formulário de login">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    aria-required="true"
                    aria-invalid={!email && email !== '' ? 'true' : 'false'}
                    data-testid="auth-signin-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Senha</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    aria-required="true"
                    aria-invalid={!password && password !== '' ? 'true' : 'false'}
                    data-testid="auth-signin-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  aria-busy={loading}
                  data-testid="auth-signin-btn"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  Entrar
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" data-testid="auth-signup-form">
              <form onSubmit={handleSignUp} className="space-y-4" aria-label="Formulário de cadastro">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    aria-required="true"
                    aria-invalid={!email && email !== '' ? 'true' : 'false'}
                    data-testid="auth-signup-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    minLength={6}
                    aria-required="true"
                    aria-invalid={password.length > 0 && password.length < 6 ? 'true' : 'false'}
                    aria-describedby="password-hint"
                    data-testid="auth-signup-password"
                  />
                  <p 
                    id="password-hint" 
                    className="text-xs text-muted-foreground"
                    data-testid="auth-signup-password-hint"
                  >
                    Mínimo 6 caracteres
                  </p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  aria-busy={loading}
                  data-testid="auth-signup-btn"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  Criar Conta
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
